import { Controller } from "@hotwired/stimulus"

const ASSET_LABELS = {
  1: "Depleted",
  2: "Critical",
  3: "Strained",
  4: "Stable",
  5: "Surplus",
  6: "Abundant"
}

const ASSET_COLORS = {
  1: "danger",
  2: "danger",
  3: "warning",
  4: "primary",
  5: "info",
  6: "success"
}

const LIABILITY_LABELS = {
  1: "Clear",
  2: "Low",
  3: "Moderate",
  4: "Elevated",
  5: "Severe",
  6: "Critical"
}

const LIABILITY_COLORS = {
  1: "success",
  2: "info",
  3: "primary",
  4: "warning",
  5: "danger",
  6: "danger"
}

const MORALE = {
  high: { label: "High", color: "success", icon: "bi-emoji-smile" },
  medium: { label: "Medium", color: "warning", icon: "bi-emoji-neutral" },
  low: { label: "Low", color: "danger", icon: "bi-emoji-frown" }
}

export default class extends Controller {
  static targets = ["row", "log"]

  connect() {
    this.rowTargets.forEach((row) => this.refresh(row))
    this.updateLocks()
  }

  get links() {
    const raw = this.element.getAttribute("data-links")
    if (!raw) return []

    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch (_error) {
      return []
    }
  }

  nudge(event) {
    event.preventDefault()
    const row = event.currentTarget.closest("[data-tracker-playground-target='row']")
    if (!row) return

    const delta = parseInt(event.currentTarget.dataset.delta, 10)
    if (Number.isNaN(delta)) return

    const blockedUp = row.dataset.lockUp === "true"
    const blockedDown = row.dataset.lockDown === "true"
    if ((delta > 0 && blockedUp) || (delta < 0 && blockedDown)) {
      this.log(row.dataset.lockReason || `${row.dataset.name} is locked.`)
      return
    }

    this.move(row, delta)
  }

  tick(event) {
    const key = event.currentTarget.dataset.key
    const row = this.rowByKey(key)
    if (!row) return

    this.log(`A day passes. ${row.dataset.name} ticks.`)
    this.move(row, parseInt(event.currentTarget.dataset.delta, 10))
  }

  setMorale(event) {
    const row = event.currentTarget.closest("[data-tracker-playground-target='row']")
    if (!row) return

    const previous = row.dataset.morale
    const next = event.currentTarget.dataset.morale
    if (previous === next) return

    row.dataset.morale = next
    this.refresh(row)
    this.log(`${row.dataset.name} morale ${this.titleCase(previous)} to ${this.titleCase(next)}.`)
  }

  reset() {
    this.rowTargets.forEach((row) => {
      row.dataset.value = row.dataset.initial
      if (row.dataset.initialMorale) row.dataset.morale = row.dataset.initialMorale
      this.refresh(row)
    })
    this.updateLocks()
    this.clearLog()
    this.log("Reset to starting values.")
  }

  move(row, delta) {
    const max = this.maxOf(row)
    const previous = this.valueOf(row)
    let next = previous + delta
    next = Math.min(max, Math.max(1, next))
    if (next === previous) return

    const rebound = row.dataset.rebound
    if (rebound === "from_max" && next === max) {
      this.applyValue(row, previous, max)
      const dest = this.reboundDestination(row, max, 1)
      if (dest !== max) {
        this.log(`${row.dataset.name} hit the max and rebounded to ${dest}.`)
        this.applyValue(row, max, dest)
      }
      return
    }
    if (rebound === "from_min" && next === 1) {
      this.applyValue(row, previous, 1)
      const dest = this.reboundDestination(row, max, max)
      if (dest !== 1) {
        this.log(`${row.dataset.name} hit the floor and rebounded to ${dest}.`)
        this.applyValue(row, 1, dest)
      }
      return
    }

    this.applyValue(row, previous, next)
  }

  reboundDestination(row, max, opposite) {
    if (row.dataset.reboundTarget === "reset") {
      const reset = parseInt(row.dataset.resetValue, 10)
      if (!Number.isNaN(reset)) return Math.min(max, Math.max(1, reset))
    }
    return opposite
  }

  applyValue(row, previous, next) {
    row.dataset.value = String(next)
    this.refresh(row)
    this.logChange(row, previous, next)
    this.fireLinks(row.dataset.key, previous, next, [])
    this.updateLocks()
  }

  logChange(row, previous, next) {
    const from = `${this.labelOf(row, previous)} (${previous})`
    const to = `${this.labelOf(row, next)} (${next})`
    this.log(`${row.dataset.name} ${from} to ${to}.`)
  }

  fireLinks(sourceKey, previous, next, path) {
    if (previous === next) return
    if (path.includes(sourceKey)) return

    const nextPath = path.concat(sourceKey)

    this.links.forEach((link) => {
      if (link.source !== sourceKey) return
      if (!this.crossed(previous, next, link.trigger)) return

      if (link.type === "react") {
        this.applyReactLink(link, sourceKey, nextPath)
      } else if (link.type === "morale") {
        this.applyMoraleLink(link, sourceKey)
      }
    })
  }

  applyReactLink(link, sourceKey, nextPath) {
    const target = this.rowByKey(link.target)
    if (!target) return
    if (nextPath.includes(link.target)) return

    const before = this.valueOf(target)
    const after = Math.min(this.maxOf(target), Math.max(1, before + link.delta))
    if (after === before) {
      this.log(`${target.dataset.name} is already at the edge. Reaction from ${this.nameOf(sourceKey)} did nothing.`)
      return
    }

    const sign = link.delta > 0 ? `+${link.delta}` : String(link.delta)
    this.log(`${this.nameOf(sourceKey)} crossed ${link.trigger}. ${target.dataset.name} ${sign}.`)
    target.dataset.value = String(after)
    this.refresh(target)
    this.fireLinks(link.target, before, after, nextPath)
  }

  applyMoraleLink(link, sourceKey) {
    const target = this.rowByKey(link.target)
    if (!target || !target.dataset.morale) return

    const order = ["low", "medium", "high"]
    const previous = target.dataset.morale
    const index = order.indexOf(previous)
    if (index < 0) return

    const nextIndex = Math.min(order.length - 1, Math.max(0, index + link.delta))
    const next = order[nextIndex]
    if (next === previous) {
      this.log(`${target.dataset.name} morale is already ${this.titleCase(previous)}. Morale link from ${this.nameOf(sourceKey)} did nothing.`)
      return
    }

    const verb = link.delta > 0 ? "improved" : "decreased"
    target.dataset.morale = next
    this.refresh(target)
    this.log(`${this.nameOf(sourceKey)} crossed ${link.trigger}. ${target.dataset.name} morale ${verb} to ${this.titleCase(next)}.`)
  }

  updateLocks() {
    const wasLocked = {}
    this.rowTargets.forEach((row) => {
      wasLocked[row.dataset.key] = row.dataset.lockUp === "true" || row.dataset.lockDown === "true"
      row.dataset.lockUp = "false"
      row.dataset.lockDown = "false"
      row.dataset.lockReason = ""
      row.dataset.lockBadge = ""
    })

    this.links.forEach((link) => {
      if (link.type !== "disable") return

      const source = this.rowByKey(link.source)
      const target = this.rowByKey(link.target)
      if (!source || !target) return

      const value = this.valueOf(source)
      const active = link.comparison === "at_or_above"
        ? value >= link.trigger
        : value <= link.trigger

      if (!active) return

      const mode = link.mode || "hard"
      if (mode === "hard" || mode === "up") target.dataset.lockUp = "true"
      if (mode === "hard" || mode === "down") target.dataset.lockDown = "true"

      const reason = link.message || this.defaultLockReason(target, source, mode)
      if (target.dataset.lockReason && !target.dataset.lockReason.includes(reason)) {
        target.dataset.lockReason = `${target.dataset.lockReason} ${reason}`
      } else if (!target.dataset.lockReason) {
        target.dataset.lockReason = reason
      }
      target.dataset.lockBadge = this.lockBadgeLabel(target)
    })

    this.rowTargets.forEach((row) => {
      const nowLocked = row.dataset.lockUp === "true" || row.dataset.lockDown === "true"
      if (nowLocked && !wasLocked[row.dataset.key] && row.dataset.lockReason) {
        this.log(row.dataset.lockReason)
      }
      if (!nowLocked && wasLocked[row.dataset.key]) {
        this.log(`${row.dataset.name} is unlocked.`)
      }
      this.refresh(row)
    })
  }

  defaultLockReason(target, source, mode) {
    if (mode === "up") return `${target.dataset.name} can't go up while locked by ${source.dataset.name}.`
    if (mode === "down") return `${target.dataset.name} can't go down while locked by ${source.dataset.name}.`
    return `${target.dataset.name} is locked by ${source.dataset.name}.`
  }

  lockBadgeLabel(row) {
    const up = row.dataset.lockUp === "true"
    const down = row.dataset.lockDown === "true"
    if (up && down) return "Locked"
    if (up) return "Locked up"
    if (down) return "Locked down"
    return "Locked"
  }

  refresh(row) {
    const value = this.valueOf(row)
    const max = this.maxOf(row)
    const inverse = row.dataset.polarity === "inverse"
    const labels = inverse ? LIABILITY_LABELS : ASSET_LABELS
    const colors = inverse ? LIABILITY_COLORS : ASSET_COLORS
    const color = colors[value] || "secondary"
    const label = labels[value] || "Unknown"
    const percent = Math.round((value / max) * 100)
    const lockedUp = row.dataset.lockUp === "true"
    const lockedDown = row.dataset.lockDown === "true"
    const locked = lockedUp || lockedDown

    const barWrap = row.querySelector("[data-bar-wrap]")
    const bar = row.querySelector("[data-bar]")
    const badge = row.querySelector("[data-label-badge]")
    const numeric = row.querySelector("[data-numeric]")
    const lockBadge = row.querySelector("[data-lock-badge]")
    const minus = row.querySelector("[data-delta='-1']")
    const plus = row.querySelector("[data-delta='1']")

    if (barWrap) barWrap.dir = inverse ? "rtl" : "ltr"
    if (bar) {
      bar.className = `progress-bar bg-${color}`
      bar.style.width = `${percent}%`
    }
    if (badge) {
      badge.className = `badge text-center me-2 flex-shrink-0 text-bg-${color}`
      badge.textContent = label
    }
    if (numeric) numeric.textContent = `${value}/${max}`

    if (lockBadge) {
      lockBadge.classList.toggle("d-none", !locked)
      const labelNode = lockBadge.querySelector("[data-lock-label]")
      if (labelNode) labelNode.textContent = row.dataset.lockBadge || this.lockBadgeLabel(row)
    }

    const minusClass = inverse ? "btn-outline-success" : "btn-outline-danger"
    const plusClass = inverse ? "btn-outline-danger" : "btn-outline-success"
    if (minus) {
      minus.classList.remove("btn-outline-success", "btn-outline-danger", "disabled")
      minus.classList.add("btn", minusClass)
      minus.classList.toggle("disabled", lockedDown)
      minus.setAttribute("aria-disabled", lockedDown ? "true" : "false")
      minus.title = lockedDown ? row.dataset.lockReason : "Decrease"
    }
    if (plus) {
      plus.classList.remove("btn-outline-success", "btn-outline-danger", "disabled")
      plus.classList.add("btn", plusClass)
      plus.classList.toggle("disabled", lockedUp)
      plus.setAttribute("aria-disabled", lockedUp ? "true" : "false")
      plus.title = lockedUp ? row.dataset.lockReason : "Increase"
    }

    const morale = row.dataset.morale
    if (morale) {
      const moraleBadge = row.querySelector("[data-morale-badge]")
      const info = MORALE[morale]
      if (moraleBadge && info) {
        moraleBadge.className = `badge text-bg-${info.color}`
        moraleBadge.textContent = info.label
      }
      row.querySelectorAll("[data-morale]").forEach((button) => {
        const tag = button.dataset.morale
        const active = tag === morale
        const map = { high: "success", medium: "warning", low: "danger" }
        button.className = `btn btn-${active ? map[tag] : `outline-${map[tag]}`}`
      })
    }
  }

  crossed(previous, next, trigger) {
    const down = previous > trigger && next <= trigger
    const up = previous < trigger && next >= trigger
    return down || up
  }

  labelOf(row, value) {
    const inverse = row.dataset.polarity === "inverse"
    const labels = inverse ? LIABILITY_LABELS : ASSET_LABELS
    return labels[value] || "Unknown"
  }

  titleCase(value) {
    if (!value) return "None"
    return value.charAt(0).toUpperCase() + value.slice(1)
  }

  valueOf(row) {
    return parseInt(row.dataset.value, 10)
  }

  maxOf(row) {
    return parseInt(row.dataset.max, 10) || 6
  }

  nameOf(key) {
    const row = this.rowByKey(key)
    return row ? row.dataset.name : key
  }

  rowByKey(key) {
    return this.rowTargets.find((row) => row.dataset.key === key)
  }

  log(message) {
    if (!this.hasLogTarget) return

    const empty = this.logTarget.querySelector("[data-log-empty]")
    if (empty) empty.remove()

    const item = document.createElement("li")
    item.textContent = message
    this.logTarget.prepend(item)

    while (this.logTarget.children.length > 8) {
      this.logTarget.removeChild(this.logTarget.lastChild)
    }
  }

  clearLog() {
    if (!this.hasLogTarget) return
    this.logTarget.innerHTML = ""
  }
}
